import React, { useState, useEffect } from 'react';
import { DollarSign, ShoppingCart, Package, TrendingUp, AlertCircle, RefreshCw, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { formatCurrency, formatNumber } from '../../utils/currency';
import { formatTableDate } from '../../utils/dateFormatter';

// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://check.policypulsehq.com/api';

const CHANNELS = [
  { label: 'All',  value: ''    },
  { label: 'FBM',  value: 'MFN' },
  { label: 'FBA',  value: 'AFN' },
];

export default function AmazonDashboard({ storeId }) {
  const [data, setData]                   = useState(null);
  const [loading, setLoading]             = useState(true);
  const [channel, setChannel]             = useState('');         // '' | 'MFN' | 'AFN'
  const [stockFilter, setStockFilter]     = useState(null);
  const [stockProducts, setStockProducts] = useState([]);
  const [loadingStock, setLoadingStock]   = useState(false);

  useEffect(() => {
    if (storeId) {
      localStorage.setItem('amazon_store_id', storeId);
      fetchDashboard(storeId, channel);
    } else {
      const saved = localStorage.getItem('amazon_store_id');
      if (saved) fetchDashboard(saved, channel);
    }
  }, [storeId, channel]);

  const fetchDashboard = async (sid, ch) => {
    try {
      setLoading(true);
      const token  = localStorage.getItem('token');
      const params = ch ? `?fulfillment_channel=${ch}` : '';
      const res    = await fetch(`${API_BASE_URL}/amazon/${sid}/dashboard${params}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const result = await res.json();
      if (result.success) setData(result.dashboard);
      else toast.error('Failed to load dashboard');
    } catch { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  };

  const handleAlertClick = async (type) => {
    if (stockFilter === type) { setStockFilter(null); setStockProducts([]); return; }
    try {
      setLoadingStock(true);
      setStockFilter(type);
      const sid   = storeId || localStorage.getItem('amazon_store_id');
      const token = localStorage.getItem('token');
      const params = channel ? `?fulfillment_channel=${channel}` : '';
      const res   = await fetch(`${API_BASE_URL}/amazon/${sid}/products${params}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const result = await res.json();
      if (result.success) {
        const filtered = type === 'out'
          ? result.products.filter(p => p.quantity === 0)
          : result.products.filter(p => p.quantity > 0 && p.quantity <= 10);
        setStockProducts(filtered);
      }
    } catch { toast.error('Failed to load stock data'); }
    finally { setLoadingStock(false); }
  };

  const channelLabel = channel === 'MFN' ? 'FBM' : channel === 'AFN' ? 'FBA' : 'All';

  if (loading) return (
    <div className="flex items-center justify-center h-screen">
      <RefreshCw className="animate-spin text-orange-600" size={48} />
    </div>
  );

  if (!data) return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-center">
        <p className="text-gray-500 text-lg mb-2">No data available</p>
        <p className="text-gray-400 text-sm">Run a full sync from Settings to get started</p>
      </div>
    </div>
  );

  const stats = [
    { title: "Today's Sales",  value: formatCurrency(data?.today?.sales_today  || 0), icon: DollarSign,  color: 'bg-gradient-to-br from-orange-500 to-orange-600', textColor: 'text-orange-600' },
    { title: "Today's Profit", value: formatCurrency(data?.today?.profit_today || 0), icon: TrendingUp,  color: 'bg-gradient-to-br from-green-500 to-green-600',  textColor: 'text-green-600'  },
    { title: 'Orders Today',   value: formatNumber(data?.today?.orders_today   || 0), icon: ShoppingCart, color: 'bg-gradient-to-br from-purple-500 to-purple-600', textColor: 'text-purple-600' },
    { title: 'Total Products', value: formatNumber(data?.inventory?.total_products || 0), icon: Package, color: 'bg-gradient-to-br from-blue-500 to-blue-600',    textColor: 'text-blue-600'   },
  ];

  return (
    <div className="p-8 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">

      {/* Header + FBA/FBM toggle */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="text-4xl font-bold text-gray-800 mb-1">
            Amazon Dashboard
            {channel && <span className={`ml-3 text-2xl font-semibold ${channel === 'AFN' ? 'text-blue-600' : 'text-orange-600'}`}>— {channelLabel}</span>}
          </h1>
          <p className="text-gray-600">Welcome to your Amazon Seller Central Management</p>
        </div>
        <div className="flex gap-2">
          {CHANNELS.map(c => (
            <button
              key={c.value}
              onClick={() => setChannel(c.value)}
              className={`px-5 py-2 rounded-lg text-sm font-semibold border transition-all duration-150 ${
                channel === c.value
                  ? c.value === 'AFN' ? 'bg-blue-600 text-white border-blue-600'
                  : c.value === 'MFN' ? 'bg-orange-600 text-white border-orange-600'
                  : 'bg-gray-700 text-white border-gray-700'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, i) => (
          <div key={i} className="bg-white rounded-xl shadow-lg overflow-hidden transform hover:scale-105 transition-transform duration-200">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <stat.icon className="text-white" size={24} />
                </div>
              </div>
              <h3 className="text-gray-500 text-sm font-medium mb-1">{stat.title}</h3>
              <p className={`text-3xl font-bold ${stat.textColor}`}>{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Inventory Alerts */}
      {(data?.inventory?.low_stock > 0 || data?.inventory?.out_of_stock > 0) && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4 rounded-r-lg">
          <div className="flex items-start">
            <AlertCircle className="text-yellow-600 mr-3 mt-0.5 flex-shrink-0" size={24} />
            <div className="flex-1">
              <h3 className="text-yellow-800 font-semibold mb-2">Inventory Alerts {channel ? `(${channelLabel})` : ''}</h3>
              <div className="flex flex-wrap gap-3">
                {data?.inventory?.low_stock > 0 && (
                  <button onClick={() => handleAlertClick('low')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all duration-150 ${
                      stockFilter === 'low' ? 'bg-yellow-500 text-white border-yellow-500' : 'bg-white text-yellow-700 border-yellow-400 hover:bg-yellow-100'
                    }`}>
                    ⚠️ {formatNumber(data.inventory.low_stock)} low in stock {stockFilter === 'low' ? '— click to hide' : '— click to view'}
                  </button>
                )}
                {data?.inventory?.out_of_stock > 0 && (
                  <button onClick={() => handleAlertClick('out')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all duration-150 ${
                      stockFilter === 'out' ? 'bg-red-500 text-white border-red-500' : 'bg-white text-red-700 border-red-400 hover:bg-red-50'
                    }`}>
                    🚫 {formatNumber(data.inventory.out_of_stock)} out of stock {stockFilter === 'out' ? '— click to hide' : '— click to view'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stock panel */}
      {stockFilter && (
        <div className={`mb-8 bg-white rounded-xl shadow-lg overflow-hidden border-t-4 ${stockFilter === 'out' ? 'border-red-400' : 'border-yellow-400'}`}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className={`text-lg font-bold ${stockFilter === 'out' ? 'text-red-700' : 'text-yellow-700'}`}>
              {stockFilter === 'out' ? '🚫 Out of Stock Products' : '⚠️ Low Stock Products (≤10 units)'}
              {channel ? ` — ${channelLabel}` : ''}
            </h2>
            <button onClick={() => { setStockFilter(null); setStockProducts([]); }} className="text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          </div>
          {loadingStock ? (
            <div className="flex justify-center py-8"><RefreshCw className="animate-spin text-gray-400" size={28} /></div>
          ) : stockProducts.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No products found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-3 px-6 text-gray-600 font-semibold">Product</th>
                    <th className="text-left py-3 px-6 text-gray-600 font-semibold">ASIN</th>
                    <th className="text-left py-3 px-6 text-gray-600 font-semibold">SKU</th>
                    <th className="text-left py-3 px-6 text-gray-600 font-semibold">Channel</th>
                    <th className="text-left py-3 px-6 text-gray-600 font-semibold">Status</th>
                    <th className="text-right py-3 px-6 text-gray-600 font-semibold">Price</th>
                    <th className="text-right py-3 px-6 text-gray-600 font-semibold">Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {stockProducts.map((p, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-6"><p className="font-medium text-gray-800">{p.product_name}</p></td>
                      <td className="py-3 px-6 font-mono text-sm text-gray-600">{p.asin || 'N/A'}</td>
                      <td className="py-3 px-6 text-gray-600">{p.seller_sku || 'N/A'}</td>
                      <td className="py-3 px-6">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${p.fulfillment_channel === 'AFN' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                          {p.fulfillment_channel === 'AFN' ? 'FBA' : 'FBM'}
                        </span>
                      </td>
                      <td className="py-3 px-6">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${p.listing_status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {p.listing_status || 'Unknown'}
                        </span>
                      </td>
                      <td className="py-3 px-6 text-right font-semibold text-orange-600">{formatCurrency(p.price)}</td>
                      <td className="py-3 px-6 text-right">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${p.quantity === 0 ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {p.quantity}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">This Month {channel ? `(${channelLabel})` : ''}</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
              <span className="text-gray-700 font-medium">Total Orders</span>
              <span className="text-2xl font-bold text-orange-600">{formatNumber(data?.month?.orders_month || 0)}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
              <span className="text-gray-700 font-medium">Total Sales</span>
              <span className="text-2xl font-bold text-green-600">{formatCurrency(data?.month?.sales_month || 0)}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
              <span className="text-gray-700 font-medium">Total Profit</span>
              <span className="text-2xl font-bold text-purple-600">{formatCurrency(data?.month?.profit_month || 0)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Top Products by Profit {channel ? `(${channelLabel})` : ''}</h2>
          <div className="space-y-3">
            {data?.top_products?.length > 0 ? data.top_products.map((p, i) => (
              <div key={i} className="flex justify-between items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-800 truncate">{p.product_name}</p>
                  <p className="text-sm text-gray-500">{formatNumber(p.units_sold)} units sold</p>
                </div>
                <span className="font-bold text-green-600 whitespace-nowrap flex-shrink-0">{formatCurrency(p.total_profit)}</span>
              </div>
            )) : <p className="text-gray-500 text-center py-4">No products data available</p>}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Recent Orders {channel ? `(${channelLabel})` : ''}</h2>
        <div className="overflow-x-auto">
          {data?.recent_orders?.length > 0 ? (
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 text-gray-600 font-semibold">Order ID</th>
                  <th className="text-left py-3 px-4 text-gray-600 font-semibold">Product</th>
                  <th className="text-left py-3 px-4 text-gray-600 font-semibold">Channel</th>
                  <th className="text-right py-3 px-4 text-gray-600 font-semibold">Revenue</th>
                  <th className="text-right py-3 px-4 text-gray-600 font-semibold">Profit</th>
                  <th className="text-left py-3 px-4 text-gray-600 font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_orders.map((order, i) => (
                  <tr key={i} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 font-medium text-gray-800">{order.amazon_order_id}</td>
                    <td className="py-3 px-4 text-gray-700">{order.product_name}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${order.fulfillment_channel === 'AFN' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                        {order.fulfillment_channel === 'AFN' ? 'FBA' : 'FBM'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right font-semibold text-orange-600">{formatCurrency(order.total_revenue)}</td>
                    <td className="py-3 px-4 text-right font-semibold text-green-600">{formatCurrency(order.net_profit)}</td>
                    <td className="py-3 px-4 text-gray-600">{formatTableDate(order.purchase_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="text-gray-500 text-center py-4">No recent orders available</p>}
        </div>
      </div>
    </div>
  );
}